import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import { masterclassService } from '../../services/masterclassService'
import NotificationBell from '../../components/NotificationBell'
import {
  BookOpen,
  FileText,
  Calendar,
  CreditCard,
  LogOut,
  Home,
  User,
  Award,
  ClipboardList,
  Menu,
  X,
  MessageSquare,
  Sparkles,
  Library,
  ChevronRight,
  Sun,
  Moon,
  Play,
  ArrowRight,
  CheckCircle2,
  Lightbulb,
  Camera,
  Monitor,
  Layers,
} from 'lucide-react'
import MyCourses from './MyCourses'
import CourseDetails from './CourseDetails'
import Assignments from './Assignments'
import Attendance from './Attendance'
import Payments from './Payments'
import Receipt from './Receipt'
import Profile from './Profile'
import Certificates from './Certificates'
import DailySchedule from './DailySchedule'
import Messages from './Messages'
import AITutorChat from './AITutorChat'
import AITutorFeedback from './AITutorFeedback'
import AIPersonalizationPage from './AIPersonalizationPage'
import StudentLibraryPage from './StudentLibraryPage'
import StudentEquipment from './Equipment'
import DigitalLabExperience from './DigitalLabExperience'
import DigitalLabWorkspace from './DigitalLabWorkspace'
import DigitalLabProjects from './DigitalLabProjects'
import DigitalLabResources from './DigitalLabResources'
import StudentProjectLab from './StudentProjectLab'
import CoursePickerModal from '../../components/student/CoursePickerModal'
import LoadingSpinner from '../../components/LoadingSpinner'
import { useTheme } from '../../context/ThemeContext'

function DashboardHome() {
  const { user } = useAuthStore()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showCoursePicker, setShowCoursePicker] = useState(false)
  const [masterclassStats, setMasterclassStats] = useState({ attended: 0, next: null })
  const [continueLearning, setContinueLearning] = useState(null) // { courseId, courseTitle, courseCode, weekNumber, lessonTitle, lessonId, completed, total, pct, isComplete }

  const loadDashboard = () =>
    api.get('/students/dashboard').then((res) => {
      setDashboardData(res.data)
      if (res.data.suggest_course_enrollment) setShowCoursePicker(true)

      // Phase 4B fallback: if schedule API returns no active courses,
      // check dashboard enrollments for 100% complete courses (status may be 'completed')
      // This handles the case where enrollment status changed to 'completed' and schedule API filtered it out
      const completeEnrollment = (res.data.enrollments || []).find(
        e => e.completion_percentage >= 100 && e.status === 'completed'
      )
      if (completeEnrollment) {
        setContinueLearning(prev => prev || {
          courseId: completeEnrollment.course_id,
          courseTitle: completeEnrollment.title,
          courseCode: completeEnrollment.course_code,
          isComplete: true,
          completed: 0,
          total: 0,
          pct: 100,
        })
      }
    })

  useEffect(() => {
    loadDashboard()
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))

    Promise.all([
      masterclassService.getMyAttendance().catch(() => ({ stats: {} })),
      masterclassService.getUpcoming().catch(() => ({ masterclass: null })),
    ]).then(([attendance, upcoming]) => {
      setMasterclassStats({
        attended: attendance?.stats?.confirmed || 0,
        next: upcoming?.masterclass || null,
      })
    })

    // Phase 4B: Fetch schedule to determine "Continue Learning" target
    api.get('/schedule/daily/' + new Date().toISOString().split('T')[0])
      .then((res) => {
        const schedules = res.data?.schedules || []

        // Pick the course requiring the most immediate action:
        // 1. Exclude 100% complete courses
        // 2. Sort by completion percentage ASC (lowest = most remaining = most immediate)
        // 3. Tie-break by most recently enrolled
        const candidates = schedules
          .filter(cs => cs.total_lessons > 0 && cs.completed_lessons < cs.total_lessons)
          .sort((a, b) => {
            const pctA = a.total_lessons > 0 ? a.completed_lessons / a.total_lessons : 0
            const pctB = b.total_lessons > 0 ? b.completed_lessons / b.total_lessons : 0
            if (pctA !== pctB) return pctA - pctB
            return new Date(b.enrollment_date || 0) - new Date(a.enrollment_date || 0)
          })

        if (candidates.length === 0) {
          // No active courses with incomplete lessons.
          // Check if any schedule course is 100% complete (still active enrollment)
          const complete = schedules.find(cs => cs.total_lessons > 0 && cs.completed_lessons >= cs.total_lessons)
          if (complete) {
            setContinueLearning({
              courseId: complete.course_id,
              courseTitle: complete.course_title,
              courseCode: complete.course_code,
              isComplete: true,
              completed: complete.completed_lessons,
              total: complete.total_lessons,
              pct: 100,
            })
          }
          // If no schedule entries at all (e.g., enrollment status = 'completed'),
          // fall back to dashboard enrollments data (loaded separately)
          return
        }

        const cs = candidates[0]
        const pct = cs.total_lessons > 0 ? Math.round((cs.completed_lessons / cs.total_lessons) * 100) : 0
        // current_lesson = first in_progress; next_lesson = first not_started
        // In Phase 4A model (completed/not_started), next_lesson is the "current" lesson to work on
        const lesson = cs.current_lesson || cs.next_lesson
        setContinueLearning({
          courseId: cs.course_id,
          courseTitle: cs.course_title,
          courseCode: cs.course_code,
          weekNumber: lesson?.week_number,
          lessonTitle: lesson?.lesson_title,
          lessonId: lesson?.lesson_id,
          moduleTitle: lesson?.module_title,
          completed: cs.completed_lessons,
          total: cs.total_lessons,
          pct,
          isComplete: false,
        })
      })
      .catch((err) => console.error('Schedule fetch error:', err))
  }, [])

  if (loading) return <LoadingSpinner label="Loading your dashboard" />

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="space-y-10">
      {showCoursePicker && (
        <CoursePickerModal
          title="Welcome! Choose Your Courses"
          subtitle="You have no active courses yet. Select one or more courses to request enrollment."
          onClose={() => setShowCoursePicker(false)}
          onEnrolled={() => loadDashboard().then(() => setShowCoursePicker(false))}
        />
      )}

      {/* Welcome */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">{greeting}</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">
            {user?.first_name} {user?.last_name}
          </h1>
          <p className="font-sans text-sm text-cinema-muted mt-2">
            {dashboardData?.enrollments?.length
              ? `You are enrolled in ${dashboardData.enrollments.length} course${dashboardData.enrollments.length !== 1 ? 's' : ''}.`
              : 'Welcome to SMA. Enroll in a course to get started.'}
          </p>
        </div>
      </div>

      {/* Phase 4B: Continue Learning */}
      {continueLearning && (
        <div className="cinema-glass border border-cinema-glow/20 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex-1 min-w-0">
              <p className="cinema-label text-cinema-glow mb-2 flex items-center gap-1.5">
                <Play size={12} /> Continue Learning
              </p>
              <h2 className="font-display text-lg sm:text-xl text-white font-medium truncate mb-1">
                {continueLearning.courseTitle}
              </h2>
              {continueLearning.isComplete ? (
                <p className="font-sans text-sm text-green-400 font-medium flex items-center gap-1.5">
                  <CheckCircle2 size={14} /> Course Complete · {continueLearning.total} lessons completed
                </p>
              ) : (
                <>
                  <p className="font-sans text-sm text-cinema-muted mb-2">
                    {continueLearning.moduleTitle && `Week ${continueLearning.weekNumber} · `}
                    {continueLearning.lessonTitle}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 max-w-[200px] h-1.5 bg-white/5">
                      <div className="h-1.5 bg-cinema-glow transition-all duration-700" style={{ width: `${continueLearning.pct}%` }} />
                    </div>
                    <p className="font-sans text-xs text-cinema-muted font-medium whitespace-nowrap">
                      {continueLearning.completed} / {continueLearning.total} lessons · {continueLearning.pct}%
                    </p>
                  </div>
                </>
              )}
            </div>
            <Link
              to={continueLearning.isComplete
                ? `/student/certificates`
                : `/student/courses/${continueLearning.courseId}${continueLearning.lessonId ? `?lesson=${continueLearning.lessonId}` : ''}`}
              className={`shrink-0 inline-flex items-center gap-2 px-5 py-3 text-sm font-sans font-medium transition-all min-h-[44px] ${
                continueLearning.isComplete
                  ? 'cinema-glass border border-green-500/30 text-green-400 hover:bg-green-500/10'
                  : 'cinema-btn-primary'
              }`}
            >
              {continueLearning.isComplete ? (
                <><Award size={16} /> View Certificate</>
              ) : (
                <>Continue Learning <ArrowRight size={16} /></>
              )}
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'My Courses', value: dashboardData?.enrollments?.length || 0, link: '/student/courses' },
          { label: 'Pending Invoices', value: dashboardData?.pending_invoices?.length || 0, link: '/student/payments' },
          { label: 'Masterclasses', value: masterclassStats.attended, link: '/student/masterclass-library' },
          { label: 'Notifications', value: dashboardData?.notifications?.length || 0, link: '/student/messages' },
        ].map(({ label, value, link }) => (
          <Link key={label} to={link} className="cinema-stat group">
            <span className="cinema-stat-label group-hover:text-cinema-gold transition-colors">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </Link>
        ))}
      </div>

      {/* Masterclass row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-white/5">
        <Link to="/student/masterclass-library" className="group">
          <span className="cinema-label block mb-2">Masterclass Library</span>
          <p className="font-display text-2xl text-purple-400 font-semibold mb-1">{masterclassStats.attended}</p>
          <p className="font-sans text-sm text-cinema-muted">Masterclasses attended · view certificates, videos &amp; resources</p>
          <ChevronRight size={16} className="mt-3 text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
        </Link>

        <div>
          <span className="cinema-label block mb-2">Next Masterclass</span>
          {masterclassStats.next ? (
            <>
              <p className="font-display text-lg text-white font-normal mb-1">{masterclassStats.next.topic}</p>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-3">
                {masterclassStats.next.event_date
                  ? new Date(masterclassStats.next.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' })
                  : ''}
              </p>
              <Link to="/masterclass" className="inline-flex items-center gap-1.5 cinema-btn-text text-xs sm:text-sm font-medium">
                Register <Calendar size={13} />
              </Link>
            </>
          ) : (
            <p className="font-sans text-sm text-cinema-muted">No upcoming masterclass scheduled.</p>
          )}
        </div>
      </div>

      {/* My Courses list */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header">
          <h2 className="cinema-section-title">My Courses</h2>
          <Link to="/student/courses" className="cinema-btn-text text-xs sm:text-sm font-medium">View all →</Link>
        </div>

        {dashboardData?.enrollments?.length > 0 ? (
          <div className="cinema-list">
            {dashboardData.enrollments.map((enrollment) => (
              <Link
                key={enrollment.id}
                to={`/student/courses/${enrollment.course_id}`}
                className="cinema-list-row group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{enrollment.title}</p>
                    <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${
                      enrollment.status === 'active'
                        ? 'bg-green-500/10 text-green-400 border-green-500/25'
                        : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25'
                    }`}>
                      {enrollment.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2.5 text-xs sm:text-sm text-cinema-muted font-medium">
                    <span className="font-sans">{enrollment.course_code}</span>
                    {enrollment.category && <span className="font-sans">· {enrollment.category}</span>}
                    {enrollment.duration_weeks && <span className="font-sans">· {enrollment.duration_weeks}w</span>}
                  </div>
                  <div className="w-full h-1.5 bg-white/5">
                    <div className="bg-cinema-glow h-1.5 transition-all duration-700" style={{ width: `${enrollment.completion_percentage}%` }} />
                  </div>
                  <p className="font-sans text-xs text-cinema-muted mt-1 font-medium">{enrollment.completion_percentage}% complete</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {enrollment.fee && (
                    <span className="font-display text-xs sm:text-sm text-cinema-glow font-medium">
                      {Number(enrollment.fee).toLocaleString()} UGX
                    </span>
                  )}
                  <ChevronRight size={16} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="cinema-empty">
            <BookOpen size={32} className="mx-auto mb-3 text-cinema-muted/20" />
            <p className="font-sans text-base text-cinema-muted mb-5">No courses enrolled yet.</p>
            <button
              type="button"
              onClick={() => setShowCoursePicker(true)}
              className="cinema-btn-primary text-xs sm:text-sm px-6 py-2.5 min-h-0"
            >
              Browse &amp; Enroll in Courses
            </button>
          </div>
        )}
      </div>

      {/* Pending Invoices */}
      {dashboardData?.pending_invoices?.length > 0 && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">Pending Payments</h2>
            <Link to="/student/payments" className="cinema-btn-text text-xs sm:text-sm font-medium">View all →</Link>
          </div>
          <div className="cinema-list">
            {dashboardData.pending_invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to="/student/payments"
                className="cinema-list-row"
              >
                <div>
                  <p className="font-sans text-sm sm:text-base text-white font-medium mb-0.5">{invoice.description || invoice.invoice_number}</p>
                  <p className="font-sans text-xs sm:text-sm text-cinema-muted">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                </div>
                <span className="font-display text-sm sm:text-base text-yellow-400 font-semibold">
                  {Number(invoice.amount).toLocaleString()} UGX
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function StudentDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const navLinks = [
    { to: '/student/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/student/courses', icon: BookOpen, label: 'My Courses' },
    { to: '/student/tutor', icon: Sparkles, label: 'AI Tutor Chat', highlight: true },
    { to: '/student/ai-learning', icon: Lightbulb, label: 'AI Learning', highlight: true },
    { to: '/student/ai-tutor', icon: Award, label: 'AI Feedback' },
    { to: '/student/schedule', icon: ClipboardList, label: 'Daily Schedule' },
    { to: '/student/assignments', icon: FileText, label: 'Assignments' },
    { to: '/student/attendance', icon: Calendar, label: 'Attendance' },
    { to: '/student/equipment', icon: Camera, label: 'Equipment' },
    { to: '/student/payments', icon: CreditCard, label: 'Payments' },
    { to: '/student/certificates', icon: Award, label: 'Certificates' },
    { to: '/student/masterclass-library', icon: Library, label: 'Masterclass Library', highlight: true },
    { to: '/student/digital-lab', icon: Monitor, label: 'Digital Lab', highlight: true },
    { to: '/student/project-lab', icon: Layers, label: 'Project Lab', highlight: true },
    { to: '/student/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/student/profile', icon: User, label: 'Profile' },
  ]

  return (
    <div className={`cinema-page min-h-screen ${darkMode ? '' : 'admin-light'}`}>
      <div className="cinema-grain" aria-hidden="true" />
      {darkMode && (
        <>
          <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-30 fixed" />
          <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-0 -left-20 opacity-20 fixed" />
        </>
      )}

      {/* Top Nav */}
      <nav className="sticky top-0 z-30 cinema-glass-strong">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 lg:hidden text-cinema-muted hover:text-white transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="SMA Student Portal"
                className="h-8"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-display text-white font-medium text-sm leading-none">Student Portal</span>
                <span className="cinema-label mt-1 text-xs">Synergy Multimedia Academy</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/student/profile"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-soft hover:text-white transition-all"
                title="My profile"
              >
                <User size={14} className="text-cinema-gold" />
                <span className="font-sans text-xs">{user?.first_name} {user?.last_name}</span>
              </Link>
              <NotificationBell />
              <button
                onClick={toggleDarkMode}
                className="flex items-center justify-center w-9 h-9 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-muted hover:text-cinema-gold transition-all"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-red-500/40 text-cinema-muted hover:text-red-400 text-xs font-sans transition-all"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed top-16 left-0 bottom-0 w-60 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
          cinema-glass-strong border-r border-white/5 p-4
          lg:static lg:translate-x-0 lg:flex-shrink-0 lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="space-y-1">
            {navLinks.map(({ to, icon: Icon, label, highlight }) => (
              <Link
                key={to}
                to={to}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-all duration-200 ${
                  location.pathname === to
                    ? 'bg-cinema-glow/10 text-cinema-glow border-l-2 border-cinema-glow'
                    : highlight
                    ? 'text-cinema-gold/80 hover:text-cinema-gold hover:bg-white/5 border-l-2 border-transparent'
                    : 'text-cinema-muted hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`}
              >
                <Icon size={16} />
                {label}
              </Link>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 min-w-0 relative ${
            location.pathname.startsWith('/student/tutor')
              ? 'p-0 overflow-hidden sm:p-6 sm:overflow-visible lg:p-8'
              : 'p-4 sm:p-6 lg:p-8'
          }`}
        >
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="courses" element={<MyCourses />} />
            <Route path="courses/:courseId" element={<CourseDetails />} />
            <Route path="tutor" element={<AITutorChat />} />
            <Route path="tutor/:sessionId" element={<AITutorChat />} />
            <Route path="ai-tutor" element={<AITutorFeedback />} />
            <Route path="ai-learning" element={<AIPersonalizationPage />} />
            <Route path="schedule" element={<DailySchedule />} />
            <Route path="assignments" element={<Assignments />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="mark-attendance" element={<Navigate to="/student/attendance?tab=check-in" replace />} />
            <Route path="payments" element={<Payments />} />
            <Route path="receipts/:paymentId" element={<Receipt />} />
            <Route path="receipts" element={<Payments />} />
            <Route path="certificates" element={<Certificates />} />
            <Route path="equipment" element={<StudentEquipment />} />
            <Route path="masterclass-library" element={<StudentLibraryPage />} />
            <Route path="messages" element={<Messages />} />
            <Route path="profile" element={<Profile />} />
            <Route path="digital-lab" element={<DigitalLabExperience />} />
            <Route path="digital-lab/resources" element={<DigitalLabResources />} />
            <Route path="digital-lab/resources/:id" element={<DigitalLabResources />} />
            <Route path="digital-lab/workspace" element={<DigitalLabWorkspace />} />
            <Route path="digital-lab/projects" element={<DigitalLabProjects />} />
            <Route path="digital-lab/projects/:id" element={<DigitalLabProjects />} />
            <Route path="project-lab" element={<StudentProjectLab />} />
            <Route path="project-lab/:enrollmentId" element={<StudentProjectLab />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
